<script setup lang="ts">
import type { WatchStopHandle } from 'vue';
import type { ObserveVisibilityConfig } from '@mop/types';
import {
  getElementUiObject,
  getLink,
  getHeadlineWithAllOptions,
  getMedia,
  getCta,
  getHtmlFromRichText,
  getHeadlineFontFromLegacy,
} from '@mop/cms/utils/utils';
import type { CmsUiFullWidthBanner } from './uiCmsFullWidthBanner';
import type UiCmsMedia from './UiCmsMedia.vue';
import type UiNuxtLink from '@mop/ui/components/global/UiNuxtLink.vue';

type NuxtLinkType = typeof UiNuxtLink;
type CmsMediaType = typeof UiCmsMedia;

defineOptions({
  name: 'UiCmsFullWidthBanner',
});

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  lazyLoad: {
    type: Boolean,
    default: false,
  },
});

const data = props.data;
const { $scroll, $breakpoint } = useNuxtApp();

const element: CmsUiFullWidthBanner = getElementUiObject(data, {
  headline: getHeadlineWithAllOptions(data),
  headlineFontStyle: data.headlineFontStyle, // Legacy, will be removed
  headlineFont: data.headlineFont,
  subline: data.subline,
  isSublineAboveHeadline: data.isSublineAboveHeadline ?? false,
  bodyText: getHtmlFromRichText(data.bodyText),
  horizontalAlignment: data.horizontalAlignment,
  ctas: data.cta?.map(getCta),
  link: getLink(data.link),
  textColor: data.textColor?.color || '#000',
  backgroundColor: data.backgroundColor?.color,
  minHeight: data.minHeight,
  media: getMedia(data.media?.[0], true),
  useParallaxEffect: data.useParallaxEffect,
  caption: data.caption,
  captionAlignment: data.captionAlignment,
  hasIndentation: data.hasIndentation,
});

if (element.media) {
  element.media.imageType = 'background';
}

const containerRef = ref<NuxtLinkType>();
const imageContainerRef = ref<CmsMediaType>();

const useParallaxEffect = element.media?.type !== 'image' ? false : (data.useParallaxEffect ?? false);
const doesContentExist = Boolean(element.headline.text || element.subline || element.ctas?.length);
const parallaxEffectRef = ref<string>();
const imageStyleRef = computed(() => (!useParallaxEffect && $breakpoint.isTinyRef ? '' : parallaxEffectRef.value));

let stopScrollWatch: WatchStopHandle;
const visibilityConfig: ObserveVisibilityConfig = {
  callback: (isVisible) => {
    if (isVisible) {
      stopScrollWatch = watch($scroll.offsetTopRef, handleScroll);
    } else {
      stopScrollWatch && stopScrollWatch();
    }
  },
  rootMargin: '0px 0px 0px 0px',
};

// Legacy fallback, remove after content fully migrated
if (!element.headlineFont) {
  element.headlineFont = getHeadlineFontFromLegacy(data.headlineFontStyle, element.headline?.level || 3);
}

onUnmounted(() => {
  stopScrollWatch && stopScrollWatch();
});

function handleLoaded() {
  if (useParallaxEffect) {
    nextTick(handleScroll);
  }
}

function handleScroll() {
  if (!containerRef?.value) {
    return;
  }
  const elementContainer = containerRef.value.$el || containerRef.value;
  const isDomElement: boolean = elementContainer instanceof HTMLElement;

  // It needs to be checked, if the container is a DOM element
  const element: Element = isDomElement ? (elementContainer as Element) : (elementContainer as NuxtLinkType).$el;
  const imageElement = imageContainerRef.value;

  const offsetTop = $scroll.offsetTopRef.value;
  const windowHeight = window.innerHeight;

  const elementPosition = element.getBoundingClientRect().top + offsetTop;
  const elementHeight = element.clientHeight;

  const windowStartScrollPosition = elementPosition - windowHeight;
  const windowStopScrollPosition = windowStartScrollPosition + windowHeight + elementHeight;
  const percentageScrolled =
    (offsetTop - windowStartScrollPosition) / (windowStopScrollPosition - windowStartScrollPosition);

  const scrollableHeight = imageElement?.$el.clientHeight - elementHeight;
  const newElementScrollTop = Math.round(percentageScrolled * scrollableHeight);

  if (newElementScrollTop < 0) {
    return;
  }

  parallaxEffectRef.value = `transform: translate3d(0, -${newElementScrollTop}px, 0);`;
}
</script>

<template>
  <div
    :class="[
      'ui-cms-full-width-banner__wrapper',
      `ui-cms-full-width-banner--${element.horizontalAlignment}`,
      {
        'ui-cms-full-width-banner--has-indentation': element.hasIndentation,
        'ui-cms-full-width-banner--no-media': element.backgroundColor && !element.media,
        'ui-cms-full-width-banner--parallax': useParallaxEffect,
        'ui-cms-full-width-banner--has-video': element.media?.type === 'vimeo',
      },
    ]"
    :style="`--background-color: ${element.backgroundColor}; --color: ${element.textColor}; --min-height: ${
      element.minHeight ?? 0
    }px;`"
  >
    <UiNuxtLink
      v-bind="element.link"
      ref="containerRef"
      v-observe-visibility="useParallaxEffect ? visibilityConfig : undefined"
      v-storyblok-editable="element"
      :class="[
        'ui-cms-full-width-banner__content',
        `ui-cms-full-width-banner__content--cta-${element.ctas?.[0]?.align || 'left'}`,
      ]"
      :data-track-element-item="`${element.media?.trackingId}`"
    >
      <div v-if="element.media" class="ui-cms-full-width-banner__background">
        <UiCmsMedia
          ref="imageContainerRef"
          :style="imageStyleRef"
          :data="element.media"
          :lazy="lazyLoad"
          scale-full-width-video
          @loaded="handleLoaded"
        />
      </div>

      <div v-if="doesContentExist" class="ui-cms-full-width-banner__textbox-wrapper">
        <div class="ui-cms-full-width-banner__textbox">
          <Ui2Heading
            v-if="element.headline.text"
            class="ui-cms-full-width-banner__textbox-headline"
            :level="element.headline.level"
            :font="element.headlineFont"
            :is-uppercase="element.headline.isUppercase"
          >
            {{ element.headline.text }}
          </Ui2Heading>
          <div
            v-if="element.subline"
            :class="[
              'ui-cms-full-width-banner__textbox-subline',
              {
                'ui-cms-full-width-banner__textbox-subline--first': element.isSublineAboveHeadline,
              },
            ]"
          >
            {{ element.subline }}
          </div>

          <div v-if="element.bodyText" v-html="element.bodyText" />

          <div v-if="element.ctas && element.ctas.length > 0" class="ui-cms-full-width-banner__textbox-ctas">
            <div v-for="cta in element.ctas" :key="cta._uid">
              <UiCmsCta :data="cta" no-padding />
            </div>
          </div>
        </div>
      </div>
    </UiNuxtLink>

    <div
      v-if="element.caption"
      :class="['ui-cms-full-width-banner__caption', `ui-cms-full-width-banner__caption--${element.captionAlignment}`]"
    >
      {{ element.caption }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ui-cms-full-width-banner__content {
  position: relative;
  display: block;
  color: var(--color);

  a {
    cursor: pointer;
  }
}

.ui-cms-full-width-banner--right {
  .ui-cms-full-width-banner__textbox-wrapper {
    text-align: right;
    left: auto;
    right: 0;
  }

  :deep(.video-player__control-small) {
    left: $space-16;
    right: auto;
  }
}

.ui-cms-full-width-banner--has-indentation {
  padding: 0 $ui-global-padding;
}

.ui-cms-full-width-banner--no-media .ui-cms-full-width-banner__textbox-wrapper {
  position: relative;
  background-color: var(--background-color);
  min-height: var(--min-height);
}

.ui-cms-full-width-banner--right .ui-cms-full-width-banner__textbox {
  .ui-cms-full-width-banner__textbox {
    text-align: right;
  }
  .ui-cms-full-width-banner__textbox-ctas {
    justify-content: flex-end;
  }
}

.ui-cms-full-width-banner--center {
  .ui-cms-full-width-banner__textbox {
    text-align: center;
  }
  .ui-cms-full-width-banner__textbox-ctas {
    justify-content: center;
  }
  .ui-cms-full-width-banner__textbox-wrapper {
    height: 100%;
    max-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    justify-self: center;
    left: auto;
  }
}

.ui-cms-full-width-banner__textbox-wrapper {
  @include v2-z(global, content);

  position: absolute;
  max-height: calc(100% - #{$space60});
  bottom: 0;
  left: 0;
  width: 50%;

  @include v2-apply-upto('desktop') {
    width: 100%;
  }
}

.ui-cms-full-width-banner--has-video .ui-cms-full-width-banner__textbox-wrapper {
  // Keep some gap for small video control button, that might appear on sides
  width: 80%;
}

.ui-cms-full-width-banner__textbox {
  display: flex;
  flex-direction: column;
  padding: $space-32 $ui-global-padding;

  @include v2-apply-upto('mobile') {
    padding: $space-16 $ui-global-padding;
  }

  &:deep(.ui-cms-cta__element) {
    padding: $space15 0;
    margin-bottom: calc($ui-global-padding * -1);
  }
}

.ui-cms-full-width-banner__textbox-subline {
  @include v2-text-style('xl');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('lg');
  }
}

.ui-cms-full-width-banner__textbox-subline--first {
  order: -1;
}

.ui-cms-full-width-banner__textbox-headline {
  margin-bottom: 0;
}

.ui-cms-full-width-banner__textbox-ctas {
  display: flex;
  gap: $space-24;
  margin-top: $space-16;
  align-items: center;

  @include v2-apply-upto('tablet') {
    gap: $space-16;
    margin-top: $space-8;
  }
}

.ui-cms-full-width-banner--parallax .ui-cms-full-width-banner__content {
  @include apply-only(medium) {
    padding: 200px 0;
  }

  @include apply-only(large) {
    padding: 215px 0;
  }

  @include apply-from(extralarge) {
    padding: 240px 0;
  }

  .ui-cms-full-width-banner__background {
    overflow: hidden;

    @include apply-from(medium) {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }
}

.ui-cms-full-width-banner__caption {
  @include v2-text-style('sm');

  padding: $space-8 $ui-global-padding;
  color: $color-text-body-primary;
}
.ui-cms-full-width-banner__caption--right {
  text-align: right;
}
.ui-cms-full-width-banner__caption--center {
  text-align: center;
}
</style>
